'use client'

import { ArrowDown, ArrowUp, Badge, Card } from '@chain-zen/ui'
import { useParams } from 'next/navigation'

import { WORKSPACE_ROUTES } from '../../constants/routes'
import type { IPublicPostData } from '../../types/post'
import { formatWeight } from '../../utils/common'
import { getTruckType } from '../../utils/shipment'
import { Appointment } from './appointment'

interface IBackhaulPublicPostCard {
  post: IPublicPostData
  workspacename?: string
  enterpriseWorkspaceID?: string | null | undefined
}

/**
 * This component is used to display a public post card in the backhaul view
 */
const BackhaulPublicPostCard = (props: IBackhaulPublicPostCard) => {
  const { post, enterpriseWorkspaceID } = props
  const params = useParams()
  const workspacename = params?.handle as string

  const workspacenameToUse = workspacename || props.workspacename || ''

  // Handle row click in desktop view to navigate to the post details
  const handleRowClick = (row: IPublicPostData) => {
    if (enterpriseWorkspaceID) {
      if (row.workspace.workspacename) {
        window.open(`${window.location.origin}/${WORKSPACE_ROUTES.PUBLIC_POSTS(row.workspace.workspacename)}/${row.id}`)
      }
    } else if (workspacenameToUse)
      window.open(`${window.location.origin}/${WORKSPACE_ROUTES.PUBLIC_POSTS(workspacenameToUse)}/${row.id}`)
  }

  const { commodity, loadNumber, weight } = post

  // Calculate the total number of stops
  const stopCount = post.stops?.length ?? 0
  let picks, drops

  if (!stopCount) {
    picks = post.numberOfPicks ? post.numberOfPicks : post.origin?.city || post.origin?.state ? 1 : 0
    drops = post.numberOfDrops ? post.numberOfDrops : post.destination?.city || post.destination?.state ? 1 : 0
  } else {
    picks = post?.stops?.length && post.stops.filter((stop) => stop.type === 'Pick').length
    drops = post?.stops?.length && post.stops.filter((stop) => stop.type === 'Drop' || stop.type === 'Both').length
  }

  const picksCount = picks === 1 ? '1 Pick, ' : `${picks} Picks, `
  const dropsCount = drops === 1 ? '1 Drop' : `${drops} Drops`
  const weightInfo = weight ? formatWeight(weight.value, weight.unit) : ''

  return (
    <Card
      className="w-full p-4 rounded-lg shadow-lg mt-2 cursor-pointer"
      onClick={() => handleRowClick(post)}
      aria-label="public-post-card"
    >
      <div className="flex justify-between items-center">
        <span className="font-bold">#{loadNumber}</span>
        <div className="flex items-center">
          {post?.truckType?.map((truck, index) => {
            const truckType = getTruckType(truck)
            if (!truckType) return null
            return (
              <Badge key={index} variant="secondary" className="text-sm mx-0.5">
                {truckType.label}
              </Badge>
            )
          })}
          {!!commodity && (
            <Badge variant="outline" className="text-sm">
              {commodity}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center mt-2">
        <span className="text-sm font-semibold">{weightInfo}</span>
        <span className="text-xs font-semibold">
          {picks ? picksCount : ''}
          {drops ? dropsCount : ''}
        </span>
      </div>

      <div className="flex items-center mt-2">
        <ArrowUp className="h-4 w-4" />
        <span>{post.origin?.city ? `${post.origin.city}, ${post.origin.stateCode}` : '--'}</span>
      </div>

      <div className="flex items-center mt-2 ml-7">
        {post.originAppointment ? (
          <Appointment fontSize="xs" appointment={post.originAppointment} />
        ) : (
          <span className="text-xs">Appointment Pending</span>
        )}
      </div>

      <div className="flex items-center mt-2">
        <ArrowDown className="h-4 w-4" />
        <span>{post.destination?.city ? `${post.destination.city}, ${post.destination.stateCode}` : '--'}</span>
      </div>

      <div className="flex items-center mt-2 ml-7">
        {post.destinationAppointment ? (
          <Appointment fontSize="xs" appointment={post.destinationAppointment} />
        ) : (
          <span className="text-xs">Appointment Pending</span>
        )}
      </div>
    </Card>
  )
}

export { BackhaulPublicPostCard }
